<script>
  import { onMount } from 'svelte';
  import { format } from 'svelte-i18n';
  import { Alert, Button, Input } from '@sveltestrap/sveltestrap';

  import { Select } from '@client/components/select';
  import { Spinner } from '@client/components/spinner';

  import { DomainsService } from '@client/services/domains';

  import { userStore } from '@client/stores/user';

  import { DOMAIN_CLONE_PROPERTIES } from '@client/enums';

  import { debounce } from '@common/utils/debounce';
  import { groupItemsByProperty } from '@common/utils/objects';

  export let error = '';
  export let updating = false;
  export let initializing = false;

  // Bindable exports - parent reads these for submit/disabled logic
  export let domainname = '';
  export let selectedDomains = [];
  export let authcode = '';
  export let cloneOptions = {
    ...Object.keys(DOMAIN_CLONE_PROPERTIES).reduce((acc, key) => {
      acc[key] = false;
      return acc;
    }, {})
  };

  // Internal state
  let domains = [];
  let matches = [];
  let filterText = '';
  let billingid = '';
  let searchingDomains = false;

  let lookupOptions = [
    {
      label: $format('label.DOMAINS'),
      value: 'domains'
    },
    {
      label: $format('label.BILLING_ID'),
      value: 'billingid'
    }
  ];

  let lookupItem = lookupOptions[0];

  const groupedOptions = groupItemsByProperty(DOMAIN_CLONE_PROPERTIES, 'group');

  export function reset() {
    domainname = '';
    authcode = '';
    selectedDomains = [];
    matches = [];
    filterText = '';
    billingid = '';
    searchingDomains = false;
    lookupItem = lookupOptions[0];
    cloneOptions = {
      ...Object.keys(DOMAIN_CLONE_PROPERTIES).reduce((acc, key) => {
        acc[key] = false;
        return acc;
      }, {})
    };
  }

  const initialize = async () => {
    initializing = true;
    const { data } = await DomainsService.getAll({ screen: $userStore.isStaff ? 'staff' : '', perPage: 250 });
    domains = data.map(({ domainname }) => ({ value: domainname, label: domainname }));
    initializing = false;
  };

  onMount(async () => await initialize());

  const processPastedDomains = (newDomain) => {
    const dn = newDomain.trim();
    const existingSelection = selectedDomains?.find(({ value }) => value === dn);
    const existingDomain = selectedDomains?.find(({ value }) => value === dn);

    if (dn.length) {
      const item = {
        value: dn,
        label: dn,
        checked: true
      };

      if (!existingSelection) {
        selectedDomains.push(item);
      }

      if (!existingDomain) {
        domains.push(item);
      }
    }
  };

  const onDomainsPaste = (e) => {
    e.preventDefault();

    const text = e.clipboardData.getData('text');
    const items = text.split(/[\s,]+/).filter(Boolean);

    if (!items.length) {
      return;
    }

    items.forEach((item) => processPastedDomains(item.trim()));

    domains = [...domains];
    selectedDomains = [...selectedDomains];
  };

  const findDomainsByBillingId = async () => {
    searchingDomains = true;

    matches = [];

    const { data } = await DomainsService.getAll({
      billingid,
      debounceBillingid: '',
      debounceQuery: '',
      page: 1,
      perPage: !billingid.length ? 1 : 1000,
      query: '',
      screen: 'staff'
    });

    searchingDomains = false;

    matches = data.map(({ domainname: dn }) => ({ value: dn, label: dn })).filter(({ value }) => domainname !== value);
  };

  const onKeyUp = debounce(async () => {
    if (!billingid.length) {
      selectedDomains = [];
      return;
    }

    if (billingid.length < 5) {
      return;
    }

    await findDomainsByBillingId();
  }, 300);

  const onAddDomain = (domain) => {
    selectedDomains = selectedDomains
      ? [...selectedDomains, { value: domain, label: domain }]
      : [{ value: domain, label: domain }];
    matches = matches.filter(({ value }) => value !== domain);
  };

  const onRemoveDomain = (domain) => {
    selectedDomains = selectedDomains.filter(({ value }) => value !== domain);
    matches = [...matches, { value: domain, label: domain }];
  };

  const onAddAll = (domains) => {
    selectedDomains = [...selectedDomains, ...domains];
    matches = [];
  };

  const onRemoveAll = () => {
    matches = [...matches, ...selectedDomains];
    selectedDomains = [];
  };

  const searchDomains = debounce(async (query) => {
    if (!query || query.length < 2) {
      return;
    }

    searchingDomains = true;

    try {
      const response = await DomainsService.getAll({
        query: query,
        screen: $userStore.isStaff ? 'staff' : '',
        perPage: 100
      });

      const data = Array.isArray(response?.data) ? response.data : [];
      const searchResults = data.map(({ domainname: dn }) => ({ value: dn, label: dn }));

      // Merge with existing domains, avoiding duplicates
      const existingValues = new Set(domains.map((d) => d.value));
      const newDomains = searchResults.filter((d) => !existingValues.has(d.value));
      domains = [...domains, ...newDomains];
    } catch (err) {
      console.error('Domain search error:', err);
    }

    searchingDomains = false;
  }, 300);

  const onFilter = (e) => {
    const filteredResults = e.detail;

    // Trigger server-side search when filtering
    if (filterText && filterText.length >= 2) {
      searchDomains(filterText);
    }
  };
</script>

<div class="clone-form">
  {#if error}
    <Alert color="danger">{@html error}</Alert>
  {/if}

  <p>{$format('label.DOMAIN')}</p>

  <Input type="text" name="domainname" bind:value={domainname} disabled />

  {#if lookupItem?.value === 'billingid'}
    <p>{$format('label.FIND_DOMAINS_BY_BILLING_ID')}</p>
  {:else}
    <p>{$format('label.SELECT_DESTINATION_DOMAINS')}</p>
  {/if}

  {#if $userStore.isStaff}
    <div class="domain-selector" role="button" tabindex="0">
      {#if lookupItem?.value === 'domains'}
        <span on:paste={onDomainsPaste}>
          <Select
            on:filter={onFilter}
            bind:filterText
            bind:value={selectedDomains}
            bind:items={domains}
            multiple
            searchable
            placeholder={$format('label.PLACEHOLDER.SELECT_DOMAINS')}
          >
            <slot name="empty" slot="empty">
              <div class="empty">
                {#if initializing || updating || searchingDomains}
                  <Spinner show inline />
                {:else}
                  {$format('label.NO_DOMAINS_MATCH')}
                {/if}
              </div>
            </slot>
          </Select>
        </span>
      {:else}
        <Input
          type="text"
          name="billingid"
          bind:value={billingid}
          on:keyup={onKeyUp}
          placeholder={$format('label.PLACEHOLDER.BILLING_ID')}
        />
      {/if}

      <Select
        items={lookupOptions}
        searchable={false}
        clearable={false}
        showChevron
        bind:value={lookupItem}
        placeholder={$format('label.PLACEHOLDER.SELECT_ACTION')}
      />
    </div>

    {#if lookupItem?.value === 'billingid' && selectedDomains?.length}
      <div class="selected-domains">
        <p>
          <span>{$format('label.SELECTED_DOMAINS')} ({selectedDomains.length.toLocaleString()})</span>
          <Button size="sm" color="secondary" on:click={() => onRemoveAll(matches)}
            >{$format('label.REMOVE_ALL')}</Button
          >
        </p>
        <ul>
          {#each selectedDomains as domain}
            <li>
              <span>{domain.label}</span>
              <Button size="sm" color="secondary" on:click={() => onRemoveDomain(domain.value)}
                >{$format('label.REMOVE')}</Button
              >
            </li>
          {/each}
        </ul>
      </div>
    {/if}

    {#if (matches?.length || searchingDomains) && lookupItem?.value === 'billingid'}
      <div class="selected-domains">
        <p>
          <span
            >{$format('label.MATCHES')}
            {#if !searchingDomains}
              ({matches.length.toLocaleString()})
            {/if}
          </span>
          <Button size="sm" color="secondary" on:click={() => onAddAll(matches)}>{$format('label.ADD_ALL')}</Button>
        </p>
        {#if searchingDomains}
          <div class="spinner">
            <Spinner show inline />
          </div>
        {:else}
          <ul>
            {#each matches as domain}
              {#if !selectedDomains?.find(({ value }) => value === domain.value)}
                <li>
                  <span>{domain.label}</span>
                  <Button size="sm" color="secondary" on:click={() => onAddDomain(domain.value)}
                    >{$format('label.ADD')}</Button
                  >
                </li>
              {/if}
            {/each}
          </ul>
        {/if}
      </div>
    {/if}
  {:else}
    <div class="domain-selector-simple" role="button" tabindex="0">
      <Select
        on:filter={onFilter}
        bind:filterText
        bind:value={selectedDomains}
        bind:items={domains}
        multiple
        searchable
        placeholder={$format('label.PLACEHOLDER.SELECT_DOMAINS')}
      >
        <slot name="empty" slot="empty">
          <div class="empty">
            {#if initializing || updating || searchingDomains}
              <Spinner show inline />
            {:else}
              {$format('label.NO_DOMAINS_MATCH')}
            {/if}
          </div>
        </slot>
      </Select>
    </div>
  {/if}

  <p>{@html $format('label.AVAILABLE_CLONING_OPTIONS')}</p>
  <div class="clone-options">
    {#each Object.entries(groupedOptions) as [group, groupConfig]}
      <div>
        <h3>{$format(group)}</h3>
        {#each Object.entries(groupConfig) as [value, config]}
          <Input
            type="checkbox"
            autocomplete="off"
            bind:checked={cloneOptions[config.value]}
            label={$format(config.label)}
          />
        {/each}
      </div>
    {/each}
  </div>

  <p>{$format('label.AUTH_CODE')}</p>
  <Input
    type="password"
    name="authcode"
    bind:value={authcode}
    disabled={initializing || updating}
    placeholder={$format('label.PLACEHOLDER.AUTH_CODE')}
  />
</div>

<style lang="scss" src="./clone-form.scss"></style>
